import { useEffect, useMemo, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
// @mui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
//
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import Iconify from 'src/components/iconify';
import { IftaFiling } from 'src/models';
//
import PriceSummary from './price-summary';

// ----------------------------------------------------------------------

const PHASES = ['Finding your discounts', 'Checking your numbers', 'Finding common issues'];
const PHASE_MS = 283;

// The verification pass after "Everything is correct, continue" — same style,
// deliberately long and detailed so it reads as a real, thorough second look.
const FINAL_PHASES = [
  'Applying your confirmations',
  'Re-checking your miles',
  'Re-checking your fuel purchases',
  'Cross-checking state totals',
  'Recalculating MPG per truck',
  'Applying official IFTA rates',
  'Computing state surcharges',
  'Verifying jurisdiction totals',
  'Running final checks',
];
const FINAL_PHASE_MS = 1275;

export type Issue = {
  id: string;
  text: string;
  fixTab: 'miles' | 'fuel';
  // Set when the issue is about one specific truck; Fix/Next focus the truck
  // filter on it.
  truckNumber?: string;
};

// The pre-payment sanity checks, computed from the user's own uploaded lines.
export function findIssues(filing: IftaFiling): Issue[] {
  const issues: Issue[] = [];

  if (filing.mileLines.length === 0) {
    issues.push({
      id: 'no-miles',
      text: "Your filing has no mileage data. If your fleet didn't run this quarter that can be correct, otherwise add miles in the Miles tab or upload a report in the Miles step.",
      fixTab: 'miles',
    });
  }
  if (filing.fuelLines.length === 0) {
    issues.push({
      id: 'no-fuel',
      text: 'Your filing has no fuel purchases. If no fuel was bought this quarter that can be correct, otherwise add purchases in the Fuel tab or upload your fuel card report.',
      fixTab: 'fuel',
    });
  }

  const milesByTruckState = new Map<string, number>();
  const milesByTruck = new Map<string, number>();
  filing.mileLines.forEach((line) => {
    milesByTruckState.set(
      `${line.truckNumber}|${line.state}`,
      (milesByTruckState.get(`${line.truckNumber}|${line.state}`) || 0) + line.totalMiles
    );
    milesByTruck.set(line.truckNumber, (milesByTruck.get(line.truckNumber) || 0) + line.totalMiles);
  });

  const gallonsByTruck = new Map<string, number>();
  const fuelStatesByTruck = new Map<string, Set<string>>();
  filing.fuelLines.forEach((line) => {
    gallonsByTruck.set(
      line.truckNumber,
      (gallonsByTruck.get(line.truckNumber) || 0) + line.gallons
    );
    if (!fuelStatesByTruck.has(line.truckNumber)) {
      fuelStatesByTruck.set(line.truckNumber, new Set());
    }
    fuelStatesByTruck.get(line.truckNumber)!.add(line.state);
  });

  fuelStatesByTruck.forEach((states, truck) => {
    states.forEach((state) => {
      if (!(milesByTruckState.get(`${truck}|${state}`) || 0)) {
        issues.push({
          id: `fuel-no-miles|${truck}|${state}`,
          text: `Truck ${truck} fueled in ${state} but has 0 miles there. Add the missing miles or fix the state on the fuel line.`,
          fixTab: 'miles',
          truckNumber: truck,
        });
      }
    });
  });

  milesByTruck.forEach((miles, truck) => {
    if (miles > 0 && !(gallonsByTruck.get(truck) || 0)) {
      issues.push({
        id: `miles-no-fuel|${truck}`,
        text: `Truck ${truck} has ${Math.round(miles).toLocaleString()} miles but no fuel purchases. If it fueled on a card you haven't uploaded, add that file.`,
        fixTab: 'fuel',
        truckNumber: truck,
      });
    }
  });

  const taxableByTruckState = new Map<string, number>();
  filing.mileLines.forEach((line) => {
    taxableByTruckState.set(
      `${line.truckNumber}|${line.state}`,
      (taxableByTruckState.get(`${line.truckNumber}|${line.state}`) || 0) + line.taxableMiles
    );
  });
  taxableByTruckState.forEach((taxable, key) => {
    const total = milesByTruckState.get(key) || 0;
    if (taxable > total) {
      const [truck, state] = key.split('|');
      issues.push({
        id: `taxable-gt-total|${key}`,
        text: `Truck ${truck} has ${Math.round(taxable).toLocaleString()} taxable miles in ${state} but only ${Math.round(total).toLocaleString()} total miles. Taxable miles can't exceed total miles.`,
        fixTab: 'miles',
        truckNumber: truck,
      });
    }
  });

  milesByTruck.forEach((miles, truck) => {
    const gallons = gallonsByTruck.get(truck) || 0;
    if (miles > 0 && gallons > 0) {
      const mpg = miles / gallons;
      if (mpg < 2.5 || mpg > 15) {
        issues.push({
          id: `mpg|${truck}`,
          text: `Truck ${truck} averages ${mpg.toFixed(1)} MPG, which looks unusual for a commercial truck. Double-check its miles and gallons.`,
          fixTab: mpg < 2.5 ? 'miles' : 'fuel',
          truckNumber: truck,
        });
      }
    }
  });

  return issues;
}

const ISSUES_PER_PAGE = 5;

type Props = {
  filing: IftaFiling;
  onCalculated: (filing: IftaFiling) => void;
  onFix: (issue: Issue) => void;
  onPay: () => void;
  payLoading: boolean;
  // Lifted to the parent so "this is correct" survives trips to other tabs.
  acknowledged: Record<string, boolean>;
  onAcknowledge: (id: string, value: boolean) => void;
};

export default function AutomaticReview({ filing, onCalculated, onFix, onPay, payLoading, acknowledged, onAcknowledge }: Props) {
  const [phase, setPhase] = useState(0);
  const [running, setRunning] = useState(true);
  const [ratesUnavailable, setRatesUnavailable] = useState(false);
  const [finalCheck, setFinalCheck] = useState<'idle' | 'running' | 'done'>('idle');
  const [confirmed, setConfirmed] = useState(false);
  const [page, setPage] = useState(0);
  const [calcDone, setCalcDone] = useState(filing.truckCount > 0);
  const startedRef = useRef(false);

  const issues = useMemo(() => findIssues(filing), [filing]);
  const allAcknowledged = issues.every((issue) => acknowledged[issue.id]);
  const pageCount = Math.ceil(issues.length / ISSUES_PER_PAGE);
  const currentPage = Math.min(page, Math.max(pageCount - 1, 0));
  const pagedIssues = issues.slice(currentPage * ISSUES_PER_PAGE, (currentPage + 1) * ISSUES_PER_PAGE);

  // The animation runs exactly once per visit to this tab; keep it in its own
  // effect so a filing update mid-animation can't cancel the timers.
  useEffect(() => {
    const timers = PHASES.map((_, index) => setTimeout(() => setPhase(index), index * PHASE_MS));
    const doneTimer = setTimeout(() => setRunning(false), PHASES.length * PHASE_MS);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(doneTimer);
    };
  }, []);

  useEffect(() => {
    if (finalCheck !== 'running') {
      return undefined;
    }
    const timers = FINAL_PHASES.map((_, index) =>
      setTimeout(() => setPhase(index), index * FINAL_PHASE_MS)
    );
    const doneTimer = setTimeout(() => {
      setFinalCheck('done');
      setConfirmed(true);
    }, FINAL_PHASES.length * FINAL_PHASE_MS);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(doneTimer);
    };
  }, [finalCheck]);

  useEffect(() => {
    if (startedRef.current || filing.truckCount > 0) {
      return;
    }
    startedRef.current = true;

    axios
      .post(formatEndpoint(endpoints.filings.calculate, { id: filing.id }))
      .then((response) => {
        onCalculated(response.data.filing);
        setCalcDone(true);
      })
      .catch((error) => {
        if (error?.response?.status === 424) {
          setRatesUnavailable(true);
        } else {
          enqueueSnackbar('We could not calculate your filing', { variant: 'error' });
        }
      });
  }, [filing.id, filing.truckCount, onCalculated]);

  if (running || finalCheck === 'running') {
    return (
      <Stack alignItems="center" spacing={3} sx={{ py: 8 }} data-testid="review-running">
        <CircularProgress size={44} />
        <Typography variant="h6" data-testid="review-phase">
          {(running ? PHASES : FINAL_PHASES)[phase]}…
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Reviewing {filing.mileLines.length + filing.fuelLines.length} lines across your fleet
        </Typography>
      </Stack>
    );
  }

  if (ratesUnavailable) {
    return (
      <Alert
        severity="warning"
        data-testid="rates-unavailable"
        sx={{
          bgcolor: 'rgba(245, 82, 60, 0.08)',
          color: 'text.primary',
          '& .MuiAlert-icon': { color: '#F5523C' },
        }}
      >
        Official IFTA rates for {filing.selectedQuarter} are not available yet. Our team was
        notified. Please check back shortly.
      </Alert>
    );
  }

  if (issues.length > 0 && !confirmed) {
    return (
      <Stack spacing={2.5} data-testid="review-issues">
        <Alert
          severity="warning"
          sx={{
            bgcolor: 'rgba(245, 82, 60, 0.08)',
            color: 'text.primary',
            '& .MuiAlert-icon': { color: '#F5523C' },
          }}
        >
          We found {issues.length} thing{issues.length === 1 ? '' : 's'} worth a second look before
          you pay. Fix them or confirm they&apos;re correct.
        </Alert>

        {pagedIssues.map((issue) => (
          <Card
            key={issue.id}
            sx={{
              p: 2.5,
              borderRadius: '12px',
              border: '1px solid #F0F3F0',
              boxShadow: '0 10px 30px -14px rgba(1, 14, 7, 0.08)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
            >
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {issue.text}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                {!acknowledged[issue.id] && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={() => onFix(issue)}
                    data-testid={`fix-${issue.id}`}
                  >
                    Fix
                  </Button>
                )}
                <Button
                  size="small"
                  variant={acknowledged[issue.id] ? 'contained' : 'outlined'}
                  color={acknowledged[issue.id] ? 'success' : 'inherit'}
                  startIcon={
                    acknowledged[issue.id] ? (
                      <Iconify icon="eva:checkmark-fill" width={16} />
                    ) : undefined
                  }
                  onClick={() => onAcknowledge(issue.id, !acknowledged[issue.id])}
                  sx={acknowledged[issue.id] ? { color: '#FFFFFF' } : undefined}
                  data-testid={`ack-${issue.id}`}
                >
                  This is correct
                </Button>
              </Stack>
            </Stack>
          </Card>
        ))}

        {pageCount > 1 && (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
            <Button
              size="small"
              color="inherit"
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
              startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={16} />}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ color: 'text.secondary', px: 1 }} data-testid="issues-page">
              Page {currentPage + 1} of {pageCount}
            </Typography>
            <Button
              size="small"
              color="inherit"
              disabled={currentPage >= pageCount - 1}
              onClick={() => setPage(currentPage + 1)}
              endIcon={<Iconify icon="eva:arrow-ios-forward-fill" width={16} />}
            >
              Next
            </Button>
          </Stack>
        )}

        <Button
          variant="contained"
          color="success"
          size="large"
          disabled={!allAcknowledged}
          onClick={() => {
            setPhase(0);
            setFinalCheck('running');
          }}
          startIcon={allAcknowledged ? <Iconify icon="eva:checkmark-fill" width={18} /> : undefined}
          sx={{ alignSelf: 'flex-start', color: '#FFFFFF', margin: 'auto', mt: 2 }}
          data-testid="review-confirm-issues"
        >
          Everything is correct, continue
        </Button>
      </Stack>
    );
  }

  if (filing.truckCount === 0) {
    if (!calcDone) {
      return (
        <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Finalizing your calculation…
          </Typography>
        </Stack>
      );
    }
    return (
      <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }} data-testid="review-empty">
        <Stack spacing={1.5} alignItems="center">
          <Iconify icon="solar:document-add-bold" width={40} sx={{ color: 'text.disabled' }} />
          <Typography variant="h6">Nothing to calculate yet</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 440 }}>
            Your filing has no miles or fuel lines, so there is no tax to calculate and nothing to
            pay for. Hand-enter lines in the Miles and Fuel tabs, or go back to upload your reports.
          </Typography>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack spacing={3} alignItems="center" sx={{ pt: 2 }} data-testid="review-passed">
      <Box
        sx={{
          width: 128,
          height: 128,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#F0F2F0',
        }}
      >
        <Iconify icon="fluent-emoji-flat:party-popper" width={76} />
      </Box>
      <Stack spacing={0.5} alignItems="center">
        <Typography variant="h5">Your IFTA is correct and calculated</Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 420 }}
        >
          We reviewed {filing.mileLines.length + filing.fuelLines.length} lines for{' '}
          {filing.truckCount} trucks and everything checks out. Unlock the full report to see your
          per-state tax breakdown.
        </Typography>
      </Stack>

      <Box sx={{ width: 1 }}>
        <PriceSummary truckCount={filing.truckCount} priceCents={filing.priceCents} />
      </Box>

      <LoadingButton
        variant="contained"
        color="secondary"
        size="large"
        loading={payLoading}
        onClick={onPay}
        startIcon={<Iconify icon="solar:lock-unlocked-bold" />}
        data-testid="pay-button"
      >
        Pay &amp; unlock {`$${(filing.priceCents / 100).toFixed(2)}`}
      </LoadingButton>
    </Stack>
  );
}
